import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

type Props = {
    index: number
    position: number
    onDelete: () => void
    onReorder: (fromIndex: number, toIndex: number) => void
    disabled: boolean
    children: React.ReactNode,
}

export default function DraggableLineItem({
    index, position, onDelete, onReorder, disabled, children,
}: Props) {
    const [draggedOver, setDraggedOver] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('fromIndex', String(index));
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setDraggedOver(index);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggedOver(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const data = e.dataTransfer.getData('fromIndex');
        const fromIndex = parseInt(data, 10);
        if (fromIndex !== index && !isNaN(fromIndex)) {
            onReorder(fromIndex, index);
        }
        setDraggedOver(null);
    };

    return (
        <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1,
                mb: 1,
                border: draggedOver === index ? '2px solid #1976d2' : '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: draggedOver === index ? '#f5f5f5' : 'transparent',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    '&:active': {
                        cursor: disabled ? 'default' : 'grabbing',
                    },
                }}
            >
                {!disabled && (
                    <Box
                        draggable
                        onDragStart={handleDragStart}
                        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, flexShrink: 0 }}
                    >
                        <DragIndicatorIcon
                            fontSize='small'
                            sx={{ color: '#9e9e9e', display: 'block' }}
                        />
                    </Box>
                )}
                <Box sx={{ flex: 1 }}>
                    <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                        Position {position}
                    </Typography>
                </Box>
                {!disabled && (
                    <IconButton
                        size='small'
                        onClick={onDelete}
                        disabled={disabled}
                        sx={{ p: 0.5, flexShrink: 0 }}
                    >
                        <ClearIcon fontSize='small' />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ pl: disabled ? 0 : 4 }}>
                {children}
            </Box>
        </Box>
    );
}
